import { on } from 'events';
import { stat } from 'fs';
import {
  MEVETH_CONTRACT,
  MEVETH_ROUTER_CONTRACT,
  SPLIT_SWAP_ROUTER_CONTRACT,
  WETH_CONTRACT,
} from '@/constants/addresses';
import { useDebounce } from '@/hooks/useDebounce';
import { supabase } from '@/lib/supabaseClient';
import {
  stakedAmount,
  stakedTxHash,
  stakedTxHashClaim,
  stakedTxHashSwapExactTokensForETH,
  stakedTxStatus,
  stakedTxStatusClaim,
  stakedTxStatusSwapExactTokensForETH,
  stakedTxSuccessMessage,
  writeTxErrorMessage,
  writeTxErrorMessageClaim,
  writeTxErrorMessageSwapExactTokensForETH,
} from '@/pages/_app';
import { Invite, UserData } from '@/pages/api/invites/getCurrentProfile';
import { device } from '@/styles/common';
import { captureCheckIn, captureException } from '@sentry/nextjs';
import { AnimatePresence, motion } from 'framer-motion';
import { useAtom } from 'jotai';
import { log } from 'next-axiom';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { use, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { formatEther, parseEther, recoverMessageAddress, UserRejectedRequestError } from 'viem';
import {
  erc20ABI,
  erc4626ABI,
  useAccount,
  useBalance,
  useContractRead,
  useContractWrite,
  useDisconnect,
  useFeeData,
  usePrepareContractWrite,
  usePublicClient,
  useSignMessage,
  useWaitForTransaction,
} from 'wagmi';
import MevEthAbi from '../../../MevEthAbi';
import { MevEthRouterAbi } from '../../../MevEthRouterAbi';
import SplitSwapRouterAbi from '../../../SplitSwapRouterAbi';
import BigWhiteButton from '../general/BigWhiteButton';
import {
  AmountContainer,
  AnimatedSpacerContainer,
  CenterFoldContainer,
  FoldInput,
  FoldSymbol,
  ManaTopText,
  MaxButton,
  TopText,
  WethSwitch,
} from '../general/CenterFold';
import MEVETHLogo from '../general/MEVETHLogo';
import {
  WithdrawalOptionCell,
  WithdrawalsOptionContainer,
  WithdrawalsOptionsContainer,
} from '../general/WithdrawalOptionCells';
import { WithdrawalsTicketContainer, WithdrawalTicketCell } from '../general/WithdrawalTicketCells';
import { SectionFAQ } from '../home/AvailableToday';
import ConfirmStakeView from './ConfirmStakeView';
import { PossibleModes } from './StakeTopFold';

type Props = {
  setMode: (mode: PossibleModes) => void;
  mode: PossibleModes;
};

function EthereumStake({ setMode, mode }: Props) {
  const { data: signMessageData, error, isLoading, signMessage, variables } = useSignMessage();
  const [code1, setCode1] = useState('');
  const [code2, setCode2] = useState('');
  const [code3, setCode3] = useState('');
  const [code4, setCode4] = useState('');
  const [code5, setCode5] = useState('');
  const ref1 = useRef<HTMLInputElement>(null);
  const ref2 = useRef<HTMLInputElement>(null);
  const ref3 = useRef<HTMLInputElement>(null);
  const ref4 = useRef<HTMLInputElement>(null);
  const ref5 = useRef<HTMLInputElement>(null);

  const [verified, setVerified] = useState(false);
  const router = useRouter();
  const inviteCode = router.query['id'];
  const [verifyLabel, setVerifyLabel] = useState('Verify me');

  const { address } = useAccount();
  useEffect(() => {
    const postMessage = async () => {
      if (variables?.message && signMessageData && address) {
        const result = await fetch('/api/invites/verifySig', {
          method: 'POST',
          body: JSON.stringify({
            message: variables?.message,
            signature: signMessageData,
            address: address,
          }),
        });
        const answer = await result.json();
        if (answer.valid && answer.user) {
          setVerified(true);
          localStorage.setItem('user', JSON.stringify(answer.user));
          router.push('/mana');
        } else {
          setVerifyLabel('Invalid signature, try with another account');
        }
        console.log('answer', answer);
      }
    };
    (async () => {
      if (variables?.message && signMessageData) {
        await postMessage();
        const recoveredAddress = await recoverMessageAddress({
          message: variables?.message,
          signature: signMessageData,
        });
        console.log('recoveredAddress', recoveredAddress);
      }
    })();
  }, [signMessageData, variables?.message]);

  useEffect(() => {
    const getUserProfile = async () => {
      const res = await fetch('/api/invites/getCurrentProfile');
      const data: UserData = await res.json();
      if (data.message === 'Success' && data.user?.address !== null) {
        router.push('/mana');
      }
    };
    getUserProfile();
  }, []);
  useEffect(() => {
    if (inviteCode) {
      setCode(1, inviteCode as string);
      console.log('inviteCode', inviteCode);
    }
  }, [inviteCode]);

  const storeInviteCode = async () => {
    const inviteCode = code1 + code2 + code3 + code4 + code5;
    const checkInvite = await fetch('/api/invites/checkInviteCode', {
      method: 'POST',
      body: JSON.stringify({ inviteCode }),
    });
    const status = checkInvite.status;
    const invite: Invite = await checkInvite.json();
    if (status !== 200) {
      return;
    }
    console.log('invite', invite);

    localStorage.setItem('inviteCode', invite.code);
    router.push('/mana/starter');
  };

  const setCode = (index: number, value: string) => {
    if (value.length > 1) {
      switch (index) {
        case 1:
          setCode1(value[0]);
          setCode(2, value.slice(1));
          ref2.current?.focus();
          break;
        case 2:
          setCode2(value[0]);

          setCode(3, value.slice(1));
          ref3.current?.focus();
          break;
        case 3:
          setCode3(value[0]);
          setCode(4, value.slice(1));
          ref4.current?.focus();
          break;
        case 4:
          setCode4(value[0]);
          setCode(5, value.slice(1));
          ref5.current?.focus();
          break;
        case 5:
          setCode5(value[0]);

          ref5.current?.focus();
          break;

        default:
          break;
      }
    } else {
      switch (index) {
        case 1:
          setCode1(value);
          break;
        case 2:
          setCode2(value);
          break;
        case 3:
          setCode3(value);
          break;
        case 4:
          setCode4(value);
          break;
        case 5:
          setCode5(value);
          break;

        default:
          break;
      }
    }
  };
  const comingSoon = true;
  if (comingSoon) {
    return (
      <CenterFoldContainer>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            flexDirection: 'column',
            padding: '30px',
          }}
        >
          <Image src="/svg/manacs.svg" width={100} height={100} alt="mana" />
          <ManaTopText style={{ textAlign: 'center', flexBasis: '50%' }}>
            <h1 style={{ fontSize: '44px' }}>Get ready for the MEV Mana launch</h1>
            <h5>
              <a href="https://www.twitter.com/mevdotio">Follow us on 𝕏</a>
              <img src="/svg/rightwhite.svg" width="20px" style={{ marginLeft: '10px' }} /> and turn
              on notifications
            </h5>
          </ManaTopText>
        </div>
      </CenterFoldContainer>
    );
  }

  if (mode === 'Confirm') {
    //! TODO: Copywriting - Fix Content
    return <ConfirmStakeView setMode={setMode} />;
  } else if (mode === 'Withdrawals') {
    return (
      <CenterFoldContainer>
        <ManaTopText>
          Early Access Airdrop
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '20px',
            }}
          >
            <input
              style={{
                height: '80px',
                width: '80px',
                backgroundColor: 'white',
                borderRadius: '20%',
                padding: '10px',
                fontSize: '64px',
                textAlign: 'center',
              }}
              maxLength={5}
              value={code1}
              onChange={(e) => {
                setCode(1, e.target.value);
              }}
              ref={ref1}
            />
            <input
              style={{
                height: '80px',
                width: '80px',
                backgroundColor: 'white',
                borderRadius: '20%',
                padding: '10px',
                fontSize: '64px',
                textAlign: 'center',
              }}
              maxLength={5}
              value={code2}
              onChange={(e) => {
                setCode(2, e.target.value);
              }}
              ref={ref2}
            />
            <input
              style={{
                height: '80px',
                width: '80px',
                backgroundColor: 'white',
                borderRadius: '20%',
                padding: '10px',
                fontSize: '64px',
                textAlign: 'center',
              }}
              maxLength={5}
              value={code3}
              onChange={(e) => {
                setCode(3, e.target.value);
              }}
              ref={ref3}
            />
            <input
              style={{
                height: '80px',
                width: '80px',
                backgroundColor: 'white',
                borderRadius: '20%',
                padding: '10px',
                fontSize: '64px',
                textAlign: 'center',
              }}
              maxLength={5}
              value={code4}
              onChange={(e) => {
                setCode(4, e.target.value);
              }}
              ref={ref4}
            />
            <input
              style={{
                height: '80px',
                width: '80px',
                backgroundColor: 'white',
                borderRadius: '20%',
                padding: '10px',
                fontSize: '64px',
                textAlign: 'center',
              }}
              maxLength={5}
              value={code5}
              onChange={(e) => {
                setCode(5, e.target.value);
              }}
              ref={ref5}
            />
          </div>
          <BigWhiteButton
            text="Redeem invite code"
            onClick={() => storeInviteCode()}
            skipConnect={true}
          ></BigWhiteButton>
        </ManaTopText>

        <ManaTopText>
          <h6>Already registered?</h6>
          <BigWhiteButton
            text={verifyLabel}
            onClick={() => {
              signMessage({ message: 'Verify me on Mev Protocol' });
            }}
          ></BigWhiteButton>
        </ManaTopText>
      </CenterFoldContainer>
    );
  }

  return <UseMevETH />;
}

function UseMevETH() {
  const [selectedProtocol, setSelectedProtocol] = useState('');

  return (
    <CenterFoldContainer>
      <TopText>You can use your mevETH to earn rewards on other protocols:</TopText>
    </CenterFoldContainer>
  );
}

const PartnerDetails = styled.div`
  margin-left: 10px;
  margin-top: 10px;
  color: white;
`;
const Partner = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: left;
  margin-top: 10px;
  align-items: left;
`;

const LogosRow = styled.div`
  justify-content: left;
  align-items: left;
  gap: 24px;
`;

const SVGImage = styled.img<{ isActive: boolean }>`
  height: 40px;
  width: 40px;
  opacity: ${({ isActive }) => (isActive ? 1 : 0.5)};
  &:hover {
    ${({ isActive }) =>
      !isActive &&
      'opacity: ${({ isActive }) => (isActive ? 1 : 0.8)}; cursor: pointer; transform: scale(1.05)'}
  }

  transition: all 0.2s ease-in-out;
`;

const ExplainerText = styled(motion.div)`
  font-size: 16px;
  font-weight: 400;
  text-align: left;
  height: 48px;
  margin-top: 16px;

  @media ${device.mobileS} {
    font-size: 14px;
  }
`;
export default function StakeCenterFold({ setMode, mode }: Props) {
  return <EthereumStake setMode={setMode} mode={mode} />;
}
